import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Masonry from 'react-masonry-css';
import { ArrowLeft, Heart } from 'lucide-react';
import { PageHero } from '@/components/PageHero';
import { CTASection } from '@/components/CTASection';
import { Lightbox } from '@/components/Lightbox';
import { BeforeAfterSlider } from '@/components/BeforeAfterSlider';
import { Reveal } from '@/components/Reveal';
import { SEO } from '@/components/SEO';
import { SkeletonList } from '@/components/SkeletonList';
import { GalleryLockedPanel } from '@/components/gallery/GalleryLockedPanel';
import { GalleryImageCard } from '@/components/gallery/GalleryImageCard';
import { ClientGalleryToolbar } from '@/components/gallery/ClientGalleryToolbar';
import { DownloadAllDialog } from '@/components/gallery/DownloadAllDialog';
import { useClientGallery } from '@/hooks/useClientGallery';
import { useAuth } from '@/context/AuthContext';
import { GALLERY_DETAIL_PAGE, BEFORE_AFTER } from '@/constants/testIds';
import NotFound from '@/pages/NotFound';

const BREAKPOINTS = { default: 3, 1024: 3, 768: 2, 500: 1 };

export default function GalleryDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const ownsGallery = Boolean(user?.role === 'client' && user.gallery_slugs?.includes(slug));

  const {
    category, notFound, isLocked, selectionLimit, images,
    favorites, selections, comments,
    unlock, toggleFavoriteFor, toggleSelectionFor, addComment, download, downloadZip,
  } = useClientGallery(slug, ownsGallery);

  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [downloadAllOpen, setDownloadAllOpen] = useState(false);

  useEffect(() => { setFavoritesOnly(false); setLightboxIndex(null); }, [slug]);

  const visibleImages = useMemo(
    () => (favoritesOnly ? images.filter((img) => favorites.has(img.id)) : images),
    [images, favoritesOnly, favorites],
  );

  if (notFound) return <NotFound />;

  const clientProps = ownsGallery
    ? { favorites, onToggleFavorite: toggleFavoriteFor, onDownload: download, selections, onToggleSelection: toggleSelectionFor, selectionLimit, comments, onAddComment: addComment }
    : {};

  return (
    <div data-testid={GALLERY_DETAIL_PAGE.root}>
      {category && <SEO title={category.name} description={category.description} image={category.cover_image_url} />}
      {category ? (
        <PageHero
          kicker={category.is_private ? 'Private Gallery' : 'Gallery'}
          title={category.name.toUpperCase()}
          subtitle={category.description}
          backgroundImage={category.cover_image_url}
          height="min-h-[46vh] lg:min-h-[52vh]"
        />
      ) : (
        <div className="min-h-[46vh] lg:min-h-[52vh] bg-[#0F0F10]" />
      )}

      <section className="bg-[#0A0A0A]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-12 lg:py-16">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
            <Link
              to={ownsGallery ? '/client' : '/galleries'}
              data-testid={GALLERY_DETAIL_PAGE.backLink}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              {ownsGallery ? 'Back to My Galleries' : 'Back to Galleries'}
            </Link>

            {ownsGallery && category && (
              <ClientGalleryToolbar
                favoriteCount={favorites.size}
                selectionCount={selections.size}
                selectionLimit={selectionLimit}
                favoritesOnly={favoritesOnly}
                onToggleFavoritesOnly={() => setFavoritesOnly((v) => !v)}
                onDownloadAll={() => setDownloadAllOpen(true)}
              />
            )}
          </div>

          {!category && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <SkeletonList count={6} className="h-72 rounded-xl bg-[#141416]" />
            </div>
          )}

          {isLocked && <GalleryLockedPanel onUnlock={unlock} />}

          {category && !isLocked && favoritesOnly && visibleImages.length === 0 && (
            <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-10 text-center" data-testid="gallery-favorites-empty">
              <Heart className="h-8 w-8 text-[hsl(var(--lxra-muted))] mx-auto mb-3" />
              <p className="text-sm text-[hsl(var(--lxra-muted))]">No favorites yet. Tap the heart on any photo to save it here.</p>
            </div>
          )}

          {category && !isLocked && visibleImages.length > 0 && (
            <div data-testid={GALLERY_DETAIL_PAGE.grid}>
              <Masonry breakpointCols={BREAKPOINTS} className="masonry-grid" columnClassName="masonry-grid_column">
                {visibleImages.map((img, idx) => {
                  const realIndex = images.indexOf(img);
                  const isSelected = selections.has(img.id);
                  return (
                    <GalleryImageCard
                      key={img.id}
                      image={img}
                      realIndex={realIndex}
                      revealDelay={(idx % 6) * 0.04}
                      galleryName={category.name}
                      onOpen={() => setLightboxIndex(realIndex)}
                      clientMode={ownsGallery}
                      isFavorite={favorites.has(img.id)}
                      isSelected={isSelected}
                      selectDisabled={selectionLimit != null && selections.size >= selectionLimit && !isSelected}
                      selectionLimit={selectionLimit}
                      onToggleFavorite={toggleFavoriteFor}
                      onToggleSelection={toggleSelectionFor}
                    />
                  );
                })}
              </Masonry>
            </div>
          )}

          {category?.before_after?.length > 0 && !isLocked && (
            <div className="mt-16" data-testid={BEFORE_AFTER.section}>
              <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--lxra-gold))] mb-1">Before &amp; After</p>
              <h2 className="font-display text-2xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] mb-6">Drag to Compare</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {category.before_after.map((pair) => (
                  <Reveal key={pair.id}>
                    <BeforeAfterSlider before={pair.before} after={pair.after} caption={pair.caption} testId={BEFORE_AFTER.slider(pair.id)} />
                  </Reveal>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {category && !isLocked && (
        <Lightbox images={images} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onNavigate={setLightboxIndex} {...clientProps} />
      )}

      {ownsGallery && (
        <DownloadAllDialog
          open={downloadAllOpen}
          onOpenChange={setDownloadAllOpen}
          totalCount={images.length}
          favoriteCount={favorites.size}
          selectionCount={selections.size}
          onDownload={downloadZip}
        />
      )}

      <CTASection testId="gallery-detail-cta-banner" buttonTestId="gallery-detail-cta-book-now-button" />
    </div>
  );
}
